import type {
  Enrollment,
  HottieContextPermission,
  InterventionStyle,
  MeProfile,
  MoveRecord,
} from '../types';
import { getBreakoutById } from '../breakouts/catalog';

export interface ImCloseContext {
  profile: MeProfile | null;
  activeEnrollment: Enrollment | null;
  recentMoves: MoveRecord[];
  style: InterventionStyle;
  triggerKey?: string;
}

export interface ImCloseAction {
  triggerKey: string;
  title: string;
  action: string;
  why: string;
  safetyClass: 'standard' | 'substance' | 'crisis_adjacent';
}

const APPS_ACTIONS: Record<string, Omit<ImCloseAction, 'triggerKey'>> = {
  open_app: {
    title: 'Close to opening again',
    action: 'Put the phone face-down. Stand up. Count to ten. Then decide if you still need the app.',
    why: 'Ten seconds is enough to break the automatic open.',
    safetyClass: 'standard',
  },
  scroll_spiral: {
    title: 'About to disappear into scrolling',
    action: 'Set a 10-minute timer. Do one offline thing until it rings. Then reassess.',
    why: 'A hard stop is easier than “just five more minutes”.',
    safetyClass: 'standard',
  },
  cancel_plans: {
    title: 'About to cancel plans',
    action: 'Text one honest line: “I’m shaky but I’m still coming.” Keep the plan unless you are unsafe.',
    why: 'Showing up half-present still breaks the isolation loop.',
    safetyClass: 'standard',
  },
  default: {
    title: 'I\'m close',
    action: 'Name the urge in one sentence. Then take one step that is not the app.',
    why: 'One achievable action beats a lecture.',
    safetyClass: 'standard',
  },
};

/**
 * Return one immediate achievable action.
 * Not a form. Not a lecture. Uses enrolled Breakout + preferred style lightly.
 */
export const resolveImCloseAction = (ctx: ImCloseContext): ImCloseAction => {
  const triggerKey = ctx.triggerKey ?? 'open_app';
  const base = APPS_ACTIONS[triggerKey] ?? APPS_ACTIONS.default;

  let action = base.action;
  if (ctx.style === 'gentle') {
    action = `${action} No drama if you slip — just come back.`;
  } else if (ctx.style === 'direct') {
    action = action.replace(/\.$/, '') + '. Do it now.';
  }

  const breakout = ctx.activeEnrollment
    ? getBreakoutById(ctx.activeEnrollment.breakoutId)
    : null;

  if (breakout?.slug === 'get-off-the-grid' && triggerKey === 'open_app') {
    action =
      ctx.style === 'gentle'
        ? 'Phone down. One breath. Walk to another room. You can open it after — first, move your body.'
        : 'Phone face-down. Leave the room. Ten seconds. Then choose.';
  }

  return {
    triggerKey,
    title: base.title,
    action,
    why: base.why,
    safetyClass: base.safetyClass,
  };
};

/**
 * Build Hottie-facing context only from explicitly permitted fields.
 * Returns null when sharing is disabled — callers must not invent context.
 */
export const buildHottieContext = (
  permission: HottieContextPermission,
  data: {
    focusAreaIds: string[];
    activeBreakoutTitle: string | null;
    goals: string[];
    momentumSummary: string[];
    triggers: string[];
    nudgeStyle: string;
  },
): Record<string, unknown> | null => {
  if (!permission.enabled) return null;

  const out: Record<string, unknown> = {
    surface: 'hottr.me',
    permissioned: true,
  };

  if (permission.shareFocusAreas) out.focusAreas = data.focusAreaIds;
  if (permission.shareActiveBreakout) out.activeBreakout = data.activeBreakoutTitle;
  if (permission.shareGoals) out.goals = data.goals;
  if (permission.shareMomentum) out.momentum = data.momentumSummary;
  if (permission.shareTriggers) out.triggers = data.triggers;
  if (permission.shareNudgePrefs) out.nudgeStyle = data.nudgeStyle;

  return out;
};
